import { useEffect, useMemo, useState } from "react";
import { Joyride, STATUS } from "react-joyride";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TOUR } from "@/constants/testIds";
import usePromoStatus from "@/lib/usePromoStatus";

/**
 * Guided product tour rendered inside the member area. Auto-starts the first
 * time a user lands on the dashboard (`user.tour_completed !== true`) and can
 * also be triggered manually via `?tour=1` from the Account page.
 *
 * Uses react-joyride 3.x — a full rewrite of the classic library with new
 * prop names (`skipBeacon` instead of `disableBeacon`, top-level `options`,
 * `onEvent` instead of `callback`, etc.).
 */
export default function OnboardingTour() {
  const { t } = useTranslation();
  const { params: P } = usePromoStatus();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [run, setRun] = useState(false);

  // Mobile layout uses a horizontal pill nav in place of the desktop
  // sidebar. Both variants render simultaneously (Tailwind toggles them
  // via `lg:hidden` / `hidden lg:block`) but only one is visible at a
  // time. To avoid react-joyride locking onto the hidden variant and
  // hanging with a black overlay + no tooltip, we point tour steps at
  // the currently-visible testId based on viewport width. Mobile pills
  // use the `-m` suffix (see `DashboardShell.SidebarLink`).
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 1024 : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const tier = user?.tier || "free";
  const shouldAutoStart = user && user !== false && user.tour_completed === false;
  const forceReplay =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("tour") === "1";

  useEffect(() => {
    if (!user || user === false) return;
    if (!shouldAutoStart && !forceReplay) return;

    // Wait until the Welcome Upgrade Modal is no longer in the DOM.
    // We used to gate on `?welcome=1` + localStorage, but that flow was
    // brittle: on mobile the modal can mount a beat later than the tour
    // effect (and the URL param can be stripped or missing entirely,
    // e.g. when the user refreshes mid-flow). Polling the DOM directly
    // is race-free and works regardless of the URL/localStorage state.
    const MODAL_SELECTOR = '[data-testid="welcome-upgrade-modal"]';
    const isModalOpen = () =>
      typeof document !== "undefined" &&
      document.querySelector(MODAL_SELECTOR) !== null;

    // Fast path — modal never opens (already dismissed, or user's tier
    // makes them ineligible). Give the dashboard one paint tick then go.
    if (!isModalOpen()) {
      const timer = setTimeout(() => {
        // Re-check on the tick — the modal mounts inside a `useEffect`
        // and can appear between our sync check and this callback.
        if (!isModalOpen()) setRun(true);
      }, 700);
      return () => clearTimeout(timer);
    }

    // Modal path — poll every 300 ms for up to 60 s, watching the DOM.
    // Once the modal unmounts (user closed it OR the checkout redirect
    // takes them off this page and the tour effect gets torn down), we
    // start the tour after a short paint delay.
    let attempts = 0;
    const iv = setInterval(() => {
      attempts += 1;
      if (!isModalOpen()) {
        clearInterval(iv);
        setTimeout(() => setRun(true), 400);
      } else if (attempts > 200) {
        // 200 * 300 ms = 60 s hard cap so we never hang forever.
        clearInterval(iv);
      }
    }, 300);
    return () => clearInterval(iv);
  }, [user, shouldAutoStart, forceReplay, tier]);

  const steps = useMemo(() => {
    // Sidebar steps switch target + tooltip placement based on viewport.
    // Desktop: canonical testId anchored to the visible `<aside>`, tooltip
    // opens to the right of the item.
    // Mobile:  `-m` suffix anchored to the horizontal pill nav, tooltip
    // opens below (there's no room to the right of a pill).
    const sideTarget = (name) =>
      isMobile
        ? `[data-testid="dashboard-sidebar-${name}-m"]`
        : `[data-testid="dashboard-sidebar-${name}"]`;
    const sidePlacement = isMobile ? "bottom" : "right";

    const base = [
      {
        target: '[data-testid="dashboard-greeting"]',
        placement: "bottom",
        title: t("tour.welcome.title"),
        content: t("tour.welcome.body"),
      },
      {
        target: '[data-testid="dashboard-metric-lessons"]',
        placement: "bottom",
        title: t("tour.metrics.title"),
        content: t("tour.metrics.body"),
      },
      {
        target: '[data-testid="dashboard-continue-card"]',
        placement: "top",
        title: t("tour.continue.title"),
        content: t("tour.continue.body"),
      },
      {
        target: sideTarget("courses"),
        placement: sidePlacement,
        title: t("tour.courses.title"),
        content: t("tour.courses.body"),
      },
      {
        target: sideTarget("articles"),
        placement: sidePlacement,
        title: t("tour.articles.title"),
        content: t("tour.articles.body"),
      },
      {
        target: sideTarget("webinars"),
        placement: sidePlacement,
        title: t("tour.webinars.title"),
        content: tier === "free"
          ? t("tour.webinars.bodyFree")
          : t("tour.webinars.body"),
      },
      {
        target: sideTarget("resources"),
        placement: sidePlacement,
        title: t("tour.resources.title"),
        content: t("tour.resources.body"),
      },
      {
        target: sideTarget("analytics"),
        placement: sidePlacement,
        title: t("tour.analytics.title"),
        content:
          tier === "free"
            ? t("tour.analytics.bodyFree")
            : tier === "basic"
            ? t("tour.analytics.bodyBasic")
            : t("tour.analytics.bodyPremium"),
      },
      {
        target: sideTarget("ai-coach"),
        placement: sidePlacement,
        title: t("tour.professor.title"),
        content:
          tier === "free"
            ? t("tour.professor.bodyFree")
            : t("tour.professor.bodyPaid"),
      },
      {
        target: sideTarget("certificates"),
        placement: sidePlacement,
        title: t("tour.certificates.title"),
        content: t("tour.certificates.body"),
      },
      {
        target: sideTarget("shop"),
        placement: sidePlacement,
        title: t("tour.shop.title"),
        content:
          tier === "premium"
            ? t("tour.shop.bodyPremium")
            : t("tour.shop.bodyLocked"),
      },
      {
        target: sideTarget("account"),
        placement: sidePlacement,
        title: t("tour.account.title"),
        content: t("tour.account.body"),
      },
    ];

    if (tier === "free") {
      // Nudge free users toward Pricing right after the professor step.
      base.splice(7, 0, {
        target: sideTarget("ai-coach"),
        placement: sidePlacement,
        title: t("tour.upgrade.title"),
        content: t("tour.upgrade.body", P),
      });
      // Swap the final "Finish" button copy so free users see the intent:
      // finishing the tour lands them on Pricing.
      const finalStep = base[base.length - 1];
      base[base.length - 1] = {
        ...finalStep,
        locale: { last: t("tour.buttons.seeMemberships") },
      };
    }

    return base;
  }, [t, tier, isMobile]);

  const persistCompletion = async () => {
    try {
      await api.post("/auth/tour/complete");
      await refresh();
    } catch { /* fail silently — non-critical */ }
    // Broadcast an explicit "tour just ended" signal so the upgrade nudge
    // modal can open *right now*, even if the user has previously
    // dismissed it (i.e. bypass the cooldown for this one moment). Using
    // a window event decouples the two components — no prop-drilling, no
    // shared ref, no race with the async refresh() above.
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("pms:tour-completed"));
    }
    if (forceReplay && typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("tour");
      window.history.replaceState({}, "", url.toString());
    }
  };

  const handleEvent = (data) => {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRun(false);
      persistCompletion();
      // After the tour ends (finished OR skipped) we stay on the
      // dashboard. The WelcomeUpgradeModal picks it up from here — it
      // waits on `user.tour_completed === true` and fires the tiered
      // upgrade + referral nudge in-place. This is cleaner than yanking
      // the user to /pricing and it keeps referral context visible.
    }
  };

  if (!user || user === false) return null;
  if (!run) return null;

  return (
    <div data-testid={TOUR.root}>
      <Joyride
        steps={steps}
        run={run}
        continuous
        scrollToFirstStep
        onEvent={handleEvent}
        options={{
          skipBeacon: true,
          buttons: ["back", "primary", "skip"],
          overlayClickAction: false,
          showProgress: true,
          primaryColor: "#1e40af",  // blue-800
          textColor: "#0f172a",
          backgroundColor: "#ffffff",
          arrowColor: "#ffffff",
          overlayColor: "rgba(15, 23, 42, 0.65)",
          zIndex: 10000,
          spotlightRadius: 12,
        }}
        locale={{
          back: t("tour.buttons.back"),
          close: t("tour.buttons.close"),
          last: t("tour.buttons.last"),
          next: t("tour.buttons.next"),
          skip: t("tour.buttons.skip"),
        }}
        styles={{
          tooltip: {
            borderRadius: 16,
            padding: 20,
            boxShadow:
              "0 20px 40px -12px rgba(15,23,42,0.35), 0 8px 16px -8px rgba(15,23,42,0.15)",
          },
          tooltipTitle: {
            fontFamily: "Outfit, ui-sans-serif, system-ui",
            fontWeight: 800,
            fontSize: 18,
            marginBottom: 6,
            color: "#0f172a",
          },
          tooltipContent: {
            padding: 0,
            lineHeight: 1.55,
            color: "#334155",
            fontSize: 14,
          },
          buttonPrimary: {
            backgroundColor: "#1e40af",
            borderRadius: 10,
            padding: "10px 18px",
            fontWeight: 600,
            fontSize: 13,
          },
          buttonBack: {
            color: "#475569",
            fontSize: 13,
            marginRight: 6,
          },
          buttonSkip: {
            color: "#94a3b8",
            fontSize: 12,
          },
        }}
      />
    </div>
  );
}
